import {Component, ElementRef, OnInit, computed, effect, inject, signal, viewChild} from '@angular/core';
import {AlertLevel} from '../../../../enums/alert-level';
import {GlobalAlertService} from '../../../../_services/global-alert.service';
import {describeAdministrationError} from '../../../../_shared/errors/administration-errors';
import {
  DemoOrganization, FiskalyDemoOrganizationHttpService
} from '../../_services/fiskaly-demo-organization-http.service';

/** A state chip: one label, one of the style guide's four tones. Same shape as the Integrationen cards'. */
interface PoolChip {
  label: string;
  tone: 'ok' | 'pending' | 'blocked' | 'neutral';
}

/**
 * **Demo-Organisationen**: the card on Owner → Integrationen that maintains the shared fiskaly TEST
 * organisations demo companies' locations are placed under (#933).
 *
 * ### Why the card exists
 *
 * „fiskaly registrieren" on a demo company's location does not create an organisation of its own: it takes a
 * slot in one of these. fiskaly lets a TEST organisation hold five TSS that are not disabled, so more than five
 * demos need a second organisation, and the operator adds it here. The chip therefore reports **free slots**,
 * the number that decides whether the next demo can be registered, and the full state is red because the next
 * „fiskaly registrieren" will be refused.
 *
 * ### Nothing is stored that is not proven TEST-only
 *
 * The server logs in with the entered key and stores the entry only when fiskaly answers TEST for exactly this
 * organisation and the organisation has no LIVE enabled. So the form has no „test the key" button: saving is
 * the test, and a refusal says which rule failed.
 *
 * ### The secret is never shown
 *
 * Like the Telegram token on the sibling card, the secret field is `type="password"`, never prefilled and
 * cleared after a successful save. The list shows the key, which is useless without the secret and is what the
 * operator matches against the dashboard.
 *
 * ### Removing asks first
 *
 * Removing an entry only deletes our record (fiskaly cannot delete an organisation through its API), and the
 * server refuses it while a demo sits in the organisation. It is still destructive for the operator's
 * bookkeeping, so it goes through the style guide's destructive-confirmation dialog, one instance driven by
 * {@link pendingRemove}.
 */
@Component({
  selector: 'app-demo-organizations',
  standalone: true,
  templateUrl: './demo-organizations.component.html'
})
export class DemoOrganizationsComponent implements OnInit {
  private readonly http = inject(FiskalyDemoOrganizationHttpService);
  private readonly alerts = inject(GlobalAlertService);

  /** The pool, or `null` while the first answer is outstanding. */
  protected readonly organizations = signal<DemoOrganization[] | null>(null);
  /** Open by default only when the pool is empty: then adding is the one thing to do here. */
  protected readonly formOpen = signal(false);
  protected readonly saving = signal(false);
  protected readonly removing = signal(false);
  /** The entry the confirmation dialog is asking about, or `null` when it is closed. */
  protected readonly pendingRemove = signal<DemoOrganization | null>(null);

  protected readonly organizationId = signal('');
  protected readonly label = signal('');
  protected readonly apiKey = signal('');
  /** Held here so a successful save can clear it: the field is never prefilled. */
  protected readonly apiSecret = signal('');

  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('cancelButton');

  /** Free slots across the pool. */
  protected readonly free = computed(() => (this.organizations() ?? []).reduce((sum, o) => sum + o.free, 0));

  /**
   * The chip, in precedence order: no answer yet → loading; empty pool → amber (nothing is wrong until a demo
   * is registered); no free slot → red (the next registration is refused); otherwise green with the number.
   */
  protected readonly chip = computed<PoolChip>(() => {
    const pool = this.organizations();
    if (pool === null) return {label: 'Status wird geprüft …', tone: 'neutral'};
    if (pool.length === 0) return {label: 'Keine eingerichtet', tone: 'pending'};
    const free = this.free();
    if (free === 0) return {label: 'Voll', tone: 'blocked'};
    return {label: free === 1 ? '1 Platz frei' : `${free} Plätze frei`, tone: 'ok'};
  });

  constructor() {
    // Abbrechen holds focus while the dialog is open: the safe answer to a destructive prompt.
    effect(() => this.cancelButton()?.nativeElement.focus());
  }

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.http.list().subscribe({
      next: pool => {
        this.organizations.set(pool);
        if (pool.length === 0) this.formOpen.set(true);
      },
      error: err => this.fail(err, 'Die Demo-Organisationen konnten nicht geladen werden.')
    });
  }

  protected toggleForm(): void {
    this.formOpen.update(open => !open);
  }

  protected add(): void {
    if (this.saving()) return;
    this.saving.set(true);

    this.http.add({
      organizationId: this.organizationId().trim(),
      label: this.label().trim(),
      apiKey: this.apiKey().trim(),
      apiSecret: this.apiSecret().trim()
    }).subscribe({
      next: added => {
        this.saving.set(false);
        this.organizations.update(pool => [...(pool ?? []), added]);
        this.organizationId.set('');
        this.label.set('');
        this.apiKey.set('');
        this.apiSecret.set('');
        this.formOpen.set(false);
        this.alerts.createAndShow('Gespeichert',
          `„${added.label}" wurde als Demo-Organisation hinzugefügt.`, AlertLevel.success);
      },
      error: err => {
        this.saving.set(false);
        this.fail(err, 'Die Demo-Organisation konnte nicht hinzugefügt werden.');
      }
    });
  }

  protected askRemove(organization: DemoOrganization): void {
    this.pendingRemove.set(organization);
  }

  protected cancelRemove(): void {
    if (this.removing()) return;
    this.pendingRemove.set(null);
  }

  protected confirmRemove(): void {
    const organization = this.pendingRemove();
    if (!organization || this.removing()) return;
    this.removing.set(true);

    this.http.remove(organization.id).subscribe({
      next: () => {
        this.removing.set(false);
        this.pendingRemove.set(null);
        this.organizations.update(pool => (pool ?? []).filter(o => o.id !== organization.id));
        this.alerts.createAndShow('Entfernt',
          `„${organization.label}" wurde aus den Demo-Organisationen entfernt.`, AlertLevel.success);
      },
      error: err => {
        this.removing.set(false);
        this.pendingRemove.set(null);
        this.fail(err, 'Die Demo-Organisation konnte nicht entfernt werden.');
      }
    });
  }

  protected value(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  /** Whole literal class strings so Tailwind emits them; the measured AA pairs from the style guide. */
  private static readonly TONES: Record<PoolChip['tone'], string> = {
    ok: 'bg-green-50 text-green-700',
    pending: 'bg-amber-50 text-amber-700',
    blocked: 'bg-red-50 text-red-700',
    neutral: 'bg-gray-100 text-gray-600'
  };

  protected chipClass(chip: PoolChip): string {
    return DemoOrganizationsComponent.TONES[chip.tone];
  }

  /** Posts a refusal as German, mapped from its `code`; the server's English `description` never renders. */
  private fail(err: unknown, fallback: string): void {
    this.alerts.createAndShow('Fehler', describeAdministrationError(err, fallback), AlertLevel.error);
  }
}
